import { Component, OnInit, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet, Router, NavigationStart, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Nav } from './shared/components/nav/nav';
import { Footer } from './shared/components/footer/footer';
import { TelaChat } from './shared/components/tela-chat/tela-chat';
import { Toast } from './shared/components/toast/toast';
import { AppState } from './core/store';
import { CatalogActions } from './core/store/catalog/catalog.actions';
import { AuthActions } from './core/store/auth/auth.actions';
import { selectSelectedAccount } from './core/store/auth/auth.selectors';
import { decodeJwt, mapClaimsToUser, IdTokenClaims } from './core/utils/jwt.utils';
import { MarketplaceTokenPersistenceService } from './core/services/marketplace-token-persistence.service';
import { MarketplaceTokenRefreshService } from './core/services/marketplace-token-refresh.service';
import { SauthTokenStore } from './core/services/sauth-token.store';
import { UserContextService } from './core/services/user-context.service';
import { MarketplaceServiceClient } from './core/services/marketplace-service-client';
import { TrafficManagerService } from './core/services/traffic-manager.service';
import { GoogleAnalyticsService } from './core/services/google-analytics.service';
import { OneTrustService } from './core/services/one-trust.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, Nav, Footer, TelaChat, Toast],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private readonly store       = inject(Store<AppState>);
  private readonly router      = inject(Router);
  private readonly persistence       = inject(MarketplaceTokenPersistenceService);
  private readonly tokenRefresh      = inject(MarketplaceTokenRefreshService);
  private readonly sauthTokenStore   = inject(SauthTokenStore);
  private readonly userContextService = inject(UserContextService);
  private readonly marketplaceServiceClient = inject(MarketplaceServiceClient);
  private readonly trafficManager  = inject(TrafficManagerService);
  private readonly googleAnalytics = inject(GoogleAnalyticsService);
  private readonly oneTrustService = inject(OneTrustService);

  private readonly selectedAccount = toSignal(this.store.select(selectSelectedAccount), { initialValue: null });

  private isPageNavigation = false;

  constructor() {
    this.trafficManager.init();

    effect(() => {
      const account = this.selectedAccount();
      if (account) {
        console.log(`[Store] selectedAccount: ${account.name} (${account.billingCrmAccountId})`);
      } else {
        console.log('[Store] selectedAccount: null');
      }
    });
  }

  ngOnInit(): void {
    this.googleAnalytics.init();
    this.oneTrustService.init();
    this.marketplaceServiceClient.checkHealth().subscribe({
      next: () => console.log('[marketplace-service] health check: OK'),
      error: (err) => console.warn('[marketplace-service] health check failed', err),
    });

    this.store.dispatch(CatalogActions.loadCatalog());
    this.store.dispatch(CatalogActions.loadProductDetails());
    this.store.dispatch(CatalogActions.loadTagMapping());

    const path = globalThis.location.pathname;
    const isAuthRoute = path.includes('/callback') || path.includes('/logout') || path.includes('/internalLogout');
    if (!isAuthRoute) {
      this.restoreAuthState();
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        const currentPath = this.router.url.split('#')[0].split('?')[0];
        const nextPath = event.url.split('#')[0].split('?')[0];
        if (currentPath !== nextPath) {
          this.isPageNavigation = true;
          document.documentElement.classList.add('no-smooth-scroll');
        }
      } else if (event instanceof NavigationEnd) {
        if (this.isPageNavigation) {
          this.isPageNavigation = false;
          setTimeout(() => document.documentElement.classList.remove('no-smooth-scroll'), 100);
        }
      }
    });
  }

  private restoreAuthState(): void {
    const persisted = this.persistence.load();
    if (!persisted) return;

    const nowSec = Date.now() / 1000;
    const isExpired = persisted.tokenExpiresAt < nowSec;

    if (isExpired) {
      if (persisted.refreshToken) {
        console.log('[Auth] Stored token expired — attempting silent refresh');
        this.tokenRefresh.schedule(0);
      } else {
        console.log('[Auth] Stored token expired and no refresh token — clearing');
        this.persistence.clear();
      }
      return;
    }

    let claims: IdTokenClaims;
    try {
      claims = decodeJwt(persisted.idToken);
    } catch {
      console.warn('[Auth] Stored id_token is malformed — clearing persisted auth state');
      this.persistence.clear();
      return;
    }

    this.store.dispatch(AuthActions.tokensStored({
      accessToken:    persisted.accessToken,
      idToken:        persisted.idToken,
      refreshToken:   persisted.refreshToken,
      tokenExpiresAt: persisted.tokenExpiresAt,
    }));
    this.store.dispatch(AuthActions.signInSuccess({ user: mapClaimsToUser(claims) }));
    this.tokenRefresh.schedule(persisted.tokenExpiresAt);

    this.sauthTokenStore.set(persisted.accessToken);
    this.userContextService.load('auth restore');
  }

}
